'use strict';

/* globals angular */

const _ = require('lodash');
const MomentTimezone = require('moment-timezone');

/** @SiteStore */
function Store (
  $http,
  $q,
  $rootScope,
  $state,
  $timeout,
  APP_DISPATCHER_ID,
  Flux,
  ResourceHelper,
  sbAuth,
  UserStore,
  AppActions,
  DashDialogsActions,
  SiteActions,
  SiteSelectorActions,
  IdentityService,
  StorageUtil
) {

  // Intialize the store
  const SiteStore = Flux.Store.create({
    name: 'SiteStore',
    events: {
      Changed: true,
      Loaded: true,
      Viewing: true,
      SiteUpdated: true,
      SiteUpdateError: true,
      ChangedWithUpdatedSite: true,
      SiteDeleted: true,
      SiteDeleteError: true,
      ChangedWithDeletedSite: true
    }
  });

  const ORG_PLAN_PUBNUB_PUBKEY = IdentityService.get('pubnub.payments');

  // Params to fetch all data on 1 request
  const _INCLUDED_MODELS = [
    'dataCaptures',
    'dataCaptures.info3d',
    'dataCaptures.dataCaptureLayers',
    'dataCaptures.processingJob.dataFiles',
    'annotations',
    'annotations.userName',
    'overlays',
    'project.users',
    'measurements',
    'measurements.userName',
  ].join(',');

  const siteOptions = {
    params: { include: _INCLUDED_MODELS },
    linkInverse: false
  };
  const noSiteOptions = {
    params: {
      include: []
    },
    linkInverse: false
  };

  const SiteResource = ResourceHelper.getResource('Site');
  const DataCaptureResource = ResourceHelper.getResource('DataCapture');

  /**
   * Initial empty store state
   * @const {object} $state
   */
  SiteStore.$state = {
    current: null,
    available: null,
    lastUpdatedSiteId: null,
    lastDeletedSiteId: null,
    timezoneId: null
  };

  /**
   * Flag to check for datacapture setted by the url Routing Logic signal
   * @type boolean
   */
  SiteStore._dataCaptureSetByUrl = null;
  SiteStore.ERROR = 'Something went wrong';

  /**
   * Intialize the store bindings and initial data.
   * @constructor
   */
  SiteStore.init = () => {

    const dispatcher = Flux.Dispatcher.get(APP_DISPATCHER_ID);

    dispatcher.register(SiteSelectorActions.SiteSelected, this._handleSiteSelected.bind(this));
    dispatcher.register(SiteActions.UpdateSite, this._handleUpdateSiteAction.bind(this));
    dispatcher.register(SiteActions.DeleteSite, this._handleDeleteSiteAction.bind(this));
  };

  /**
   * Sets the internal state for the Store (used by the Router logic to place the current Site).
   * @param {object} [state] An SiteStore state object.
   */
  SiteStore.setState = (state) => {

    angular.extend(SiteStore.$state, state);
    SiteStore.emit(SiteStore.Events.Changed);
  };

  /**
   * Determines the default site for an specified user.
   * The rule is: grab the last visited site for the user, and store that value
   * in a cookie. Renew the cookie each time the user visits a new site.
   * Since the implementation depends on the user navigation and no data, this
   * method was moved away from the Resource
   * If the user doesn't have any accessible sites, return null as the promise
   * value
   * @param {Resource} user The user requesting the site
   */
  SiteStore.getDefaultSite = (user, options) => {

    // Workaround for the JSData slow relationships, see [CORE-845]
    const allSites = user.userSites;
    let _lastVisited = IdentityService.get('lastVisitedSiteId');

    if (_lastVisited) {
      _lastVisited = parseInt(_lastVisited, 10);

      if (_.find(allSites, 'id', _lastVisited)) {
        return SiteResource.find(_lastVisited, options);
      }
    }

    // Not found in local storage, defaulting to first site
    const defaultSite = allSites[0];

    if (defaultSite) {
      return SiteResource.find(defaultSite.id, options);
    }
    else {
      // Case where the user doesn't have any accessible sites
      return $q.resolve(null);
    }
  };

  SiteStore._siteSettingCanShareCache = {};
  SiteStore.getSiteSettingsCanShare = function(id) {

    const siteId = id ? id : SiteStore.get('current').id;

    return new Promise((resolve, reject) => {

      if (!siteId) {
        return resolve({ canShareSite: false });
      }

      // is the share setting already cached?
      if (!_.isUndefined(SiteStore._siteSettingCanShareCache[siteId])) {
        return resolve({
          siteId: siteId,
          canShareSite: SiteStore._siteSettingCanShareCache[siteId]
        });
      }

      // this siteId is undefined, let's make it null to prevent a 2nd request
      SiteStore._siteSettingCanShareCache[siteId] = null;

      // 37: AC2
      const SITE_SHARE_BLACKLIST = [37];
      if (SITE_SHARE_BLACKLIST.indexOf(siteId) > -1) {
        SiteStore._siteSettingCanShareCache[siteId] = false;
        return resolve({
          siteId: siteId,
          canShareSite: SiteStore._siteSettingCanShareCache[siteId]
        });
      }

      const url = `${IdentityService.getApiUri()}sites/${siteId}/settings`;

      sbAuth.getToken().then((token) => {
        return $http({
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          url
        })
        .then((res) => {
          const lodashPermission = _.chain(res).get(['data', 'data']).find({key: 'sharing'});
          SiteStore._siteSettingCanShareCache[siteId] = !lodashPermission.has('value').value() || (lodashPermission.get('value').value() === 'enabled');

          return resolve({
            siteId: siteId,
            canShareSite: SiteStore._siteSettingCanShareCache[siteId]
          });
        })
        .catch(reject);
      });
    });
  };

  SiteStore.updateSiteSettingsCanShare = function(siteId, newValue) {

    return new Promise((resolve, reject) => {
      sbAuth.getToken().then(function(token) {
        return $http({
          method: 'POST',
          data: {
            key: 'sharing',
            value: newValue ? 'enabled' : 'disabled'
          },
          headers: {
            'Authorization': 'Bearer ' + token
          },
          url: IdentityService.getApiUri() + 'sites/' + siteId + '/settings/'
        }).then(() => {
          SiteStore._siteSettingCanShareCache[siteId] = newValue;

          resolve({
            siteId: siteId,
            canShareSite: newValue
          });
        });
      }).catch(reject);
    });
  };

  const getGoogleTimezone = (lat, lng) => {

    return $http.get(`https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${new Date().getTime() / 1000}`);
  };

  /**
   * Provides the routing logic with a way to seed the new state for the Store
   * it signals the intent to load an specific site with an specific datacapture
   * It has the following responsibilities:
   *   - Determining the site to fetch from the URL (specified or default)
   *   - Determining the data capture to fetch (specified or default)
   *   - (future: determining other initial data from the URL (snaps))
   *   - Fetching the site with all the related resources (or show an error
   *     if user is not authorized to view the site)
   *   - Sending the events to the appropiate stores (SiteStore, DCStore, ...)
   *     to set the *current* value (available will be obtained through watching
   *     the parent related object)
   *
   * @param {object} [stateParams] An state defining the requested site and datacap
   * {changeToLocation: null, siteName: null, dataCaptureId: null}
   * @returns {object} Object with the information needed for the initial load,
   * i.e. site (and siteName, needed for url), dataCaptureId, (annotation ids, ...)
   */
  SiteStore.fetchNewStateData = (stateParams) => {

    let fetchSitePromise;

    //check for routing params
    if (stateParams.siteName) {
      fetchSitePromise = SiteResource.findBySlug(stateParams.siteName, siteOptions);
    }
    // no site to set, fetch default
    else {
      const user = UserStore.get('user');
      fetchSitePromise = SiteStore.getDefaultSite(user, siteOptions);
    }

    var fetchNewStateDataReturnValue = fetchSitePromise
    .catch((error) => {

      // Error fetching site, either name is incorrect or user doesn't have
      // permissions
      if (error.status && error.status === 404) {
        return $q.reject(SiteStore.ERROR);
      }
    })
    .then((site) => {

      const timezoneId = StorageUtil.get('timezoneId-' + site.id);
      if (timezoneId) {
        SiteStore.$state.timezoneId = timezoneId;
        SiteStore.$state.timezoneOffset = StorageUtil.get('timezoneOffset-' + site.id);
      }
      else {

        const lat = site.location && site.location.lat;
        const lng = site.location && site.location.lng;

        if (lat && lng) {
          return getGoogleTimezone(lat, lng)
          .then((timezone) => {

            const detectedTimezoneId = _.get(timezone, ['data', 'timeZoneId']);

            if (detectedTimezoneId) {

              const detectedTimezoneOffset = (
                _.get(timezone, ['data', 'rawOffset']) +
                _.get(timezone, ['data', 'dstOffset'])
              ) / 60;

              StorageUtil.set('timezoneId-' + site.id, detectedTimezoneId);
              StorageUtil.set('timezoneOffset-' + site.id, detectedTimezoneOffset);
              SiteStore.$state.timezoneId = detectedTimezoneId;
              SiteStore.$state.timezoneOffset = detectedTimezoneOffset;
            }
            else {
              // Reset state
              SiteStore.$state.timezoneId = null;
              SiteStore.$state.timezoneOffset = null;
            }

          })
          .catch((err) => {

            SiteStore.$state.timezoneId = null;
            SiteStore.$state.timezoneOffset = null;
          })
          .then(() => {
            return site;
          })
        }
      }

      return site;
    })
    .then((site) => {
      if (!site) {
        return $q.reject(SiteStore.ERROR);
      }

      // Save the site in local storage for future retrieval
      IdentityService.set('lastVisitedSiteId', site.id);
      IdentityService.set('lastVisitedSiteSlug', site.slug);

      const dataCaptureFromUrl = parseInt(stateParams.dataCaptureId, 10);
      let currentDataCapture;

      //if datacap set by url
      if (dataCaptureFromUrl) {

        //check datacapture existence in current available
        const dataCapturePresent = site.dataCaptures.some((dataCapture) => {
          return dataCapture.id === dataCaptureFromUrl;
        });

        //if present save in key for DataCaptureStore
        if (dataCapturePresent) {
          currentDataCapture = DataCaptureResource.get(dataCaptureFromUrl);
        }
        else {
          // Not authorized or dc doesn't exist
          console.error('Requested DataCapture not available');
        }
      }
      else {
        // TODO: site.getDefaultDataCapture could be asyn if dcs are not fetched,
        // should return a promise.
        currentDataCapture = site.getDefaultDataCapture();
      }

      // update state
      const current = SiteStore.get('current');

      if (!current || current.id !== site.id) {
        // Only broadcast state change when site itself changed
        SiteStore.setState({
          current: site
        });
      }

      const DataCaptureStore = Flux.Store.get('DataCaptureStore');
      DataCaptureStore.setState({
        current: currentDataCapture
      });

      return {
        site,
        dataCapture: currentDataCapture
      };
    });

    return fetchNewStateDataReturnValue;
  };

  SiteStore.syncSubscribe = () => {

    const SyncStore = Flux.Store.get('SyncStore');
    const currentOrganizationId = IdentityService.get('currentOrganization').id;

    // Very slow and blocking
    // SiteStore._fetchUserSitesIfNeeded();

    const _isAffectedSiteOnMapView = (current, updatedSite) => {

      const isDashMapView = $state.is('dash.map');
      const currentIsUpdatedSite = (current.id === updatedSite.id);

      return isDashMapView && currentIsUpdatedSite;
    };

    const _slugChanged = (current, updatedSite) => {

      const isOnMapView = _isAffectedSiteOnMapView(current, updatedSite);
      const doSlugChanged = (current.slug !== updatedSite.slug);

      return isOnMapView && doSlugChanged;
    };

    const _userHasSite = (user, siteId) => {

      if (!user) {
        return false;
      }

      return _.find(user.userSites, { id: siteId });
    };

    const handleSyncSiteUpdated = (message) => {

      const site = message.site;
      const user = UserStore.get('user');
      const current = SiteStore.get('current') || { };
      const lastUpdatedSiteId = SiteStore.get('lastUpdatedSiteId');

      if (!_userHasSite(user, site.id)) {
        return;
      }

      if (lastUpdatedSiteId !== site.id) {
        SiteStore._syncStoreWithUpdatedSite(site);
      }

      if (_slugChanged(current, site)) {
        DashDialogsActions.SiteSlugChanged.dispatch(site);
      }

      SiteStore.$set('lastUpdatedSiteId', null);
    };

    const handleSyncSiteDeleted = (message) => {

      const site = message.site;
      const user = UserStore.get('user');
      const current = SiteStore.get('current') || {};
      const lastDeletedSiteId = SiteStore.get('lastDeletedSiteId');

      if (!_userHasSite(user, site.id)) {
        return;
      }

      if (lastDeletedSiteId !== site.id) {
        SiteStore._syncStoreWithDeletedSite(site);
      }

      if (_isAffectedSiteOnMapView(current, site)) {
        DashDialogsActions.SiteDeleted.dispatch(site);
      }

      SiteStore.$set('lastDeletedSiteId', null);
    };

    if (IdentityService.isAdminUser()) {
      SyncStore.subscribe(ORG_PLAN_PUBNUB_PUBKEY, 'adminOrgSiteUpdated', { message: handleSyncSiteUpdated });
      SyncStore.subscribe(ORG_PLAN_PUBNUB_PUBKEY, 'adminOrgSiteDeleted', { message: handleSyncSiteDeleted });
    }
    else {
      SyncStore.subscribe(ORG_PLAN_PUBNUB_PUBKEY, `orgSiteUpdated-${currentOrganizationId}`, { message: handleSyncSiteUpdated });
      SyncStore.subscribe(ORG_PLAN_PUBNUB_PUBKEY, `orgSiteDeleted-${currentOrganizationId}`, { message: handleSyncSiteDeleted });
    }
  };

  // Very slow and blocing
  // SiteStore._fetchUserSitesIfNeeded = async () => {

  //   $timeout.cancel(SiteStore._fetchUserSitesTimeout);

  //   SiteStore._fetchUserSitesTimeout = $timeout(() => {
  //     console.log('_fetchUserSitesTimeout');
  //     const UserResource = ResourceHelper.getResource('User');
  //     const user = IdentityService.getUser();
  //     const orgId = IdentityService.get('currentOrganization').id;

  //     if (_.has(UserStore.get('user'), 'userSites')) {
  //       return;
  //     }

  //     await UserResource.find(user.id, {
  //       params: {
  //         orgId,
  //         include: 'userSites,organizations',
  //         admin: user.isAdmin,
  //       },
  //       bypassCache: true
  //     })
  //     .then((res) => {

  //       UserStore.setUser(res);
  //     });
  //   }, 1000);
  // };

  SiteStore._syncStoreWithUpdatedSite = (site) => {

    let current = SiteStore.get('current');
    let available = SiteStore.get('available');
    const updatedSite = _.find(available, { id: site.id });

    if ((current && current.id) === site.id) {
      Object.assign(current, site);
    }

    if (updatedSite) {
      if (updatedSite.name !== site.name) {
        AppActions.DisplayGrowl.dispatch(`The site “${site.name}” has been renamed`, {
          type: 'success',
          id: `site-updated-${site.id}`
        });
      }

      Object.assign(updatedSite, site);
    }


    SiteResource.inject(site);
    SiteStore.setState({ current, available });
    SiteStore.emit(SiteStore.Events.ChangedWithUpdatedSite, site);
  };

  SiteStore._syncStoreWithDeletedSite = (site) => {

    let current = SiteStore.get('current');
    let available = SiteStore.get('available');
    const deletedSite = _.find(available, 'id', site.id);

    if (deletedSite) {
      if ((current && current.id) === deletedSite.id) {
        current = null;
      }

      available = _.reject(available, { 'id': deletedSite.id });

      AppActions.DisplayGrowl.dispatch(`The site “${deletedSite.name}” has been deleted`, {
        type: 'alert',
        id: `site-updated-${deletedSite.id}`
      });

      SiteResource.eject(deletedSite);
      SiteStore.setState({ current, available });
    }

    SiteStore.emit(SiteStore.Events.ChangedWithDeletedSite, site);
  };

  // Stores Handlers //////////////////////////////////////////////////////////////////
  /**
   * Handler for the UserStore change, to fetch available sites for the current user
   */
  this._handleUserStoreChanged = () => {

    const user = UserStore.get('user');

    SiteStore.setState({
      available: _.clone(user.userSites)
    });

    SiteStore.syncSubscribe();
  };

  // Actions Handlers //////////////////////////////////////////////////////////////////
  /**
   * Handler for the select site action (typically from the site selector)
   * @param {object} [event] An object containing the selected site in the data index
   */
  this._handleSiteSelected = (ev) => {

    const current = SiteStore.get('current');

    if (current.id === ev.data.siteId) {
      // Do nothing when changing to the same site (e.g: loaded default datacapture)
      return;
    }

    $rootScope.$emit('new_site_selected', {
      current: current.id,
      new: ev.data.siteId
    });

    // Use #refresh instead of #find to ensure we load the whole models, in case
    // they were requested partially before (see: SiteResource#getDefaultDataCapture)
    SiteResource.refresh(ev.data.siteId, noSiteOptions)
    .then((data) => {
      SiteStore.setState({
        current: data
      });

      let url = `site/${data.slug}`;
      const params = {
        siteName: data.slug,
        changeToLocation: url,
        urlUpdate: false
      };

      if (ev.data.defaultDataCapture) {
        params.dataCaptureId = ev.data.defaultDataCapture.id;
        url = `${url}/dc/${ev.data.defaultDataCapture.id}`;
      }

      $state.go('dash.map', params, {
        notify: false,
        reload: true
      });
    });
  };

  this._handleUpdateSiteAction = (ev) => {

    return SiteResource.update(ev.id, ev.data)
    .then((site) => {

      SiteStore.$set('lastUpdatedSiteId', site.id);
      SiteStore._syncStoreWithUpdatedSite(site);
      SiteStore.emit(SiteStore.Events.SiteUpdated, site);
    })
    .catch(() => {

      SiteStore.emit(SiteStore.Events.SiteUpdateError);
    });
  };

  this._handleDeleteSiteAction = (ev) => {

    return SiteResource.destroy(ev.id)
    .then((siteId) => {

      SiteStore.$set('lastDeletedSiteId', siteId);
      SiteStore._syncStoreWithDeletedSite(ev.site);
      SiteStore.emit(SiteStore.Events.SiteDeleted, siteId);
    })
    .catch(() => {

      SiteStore.emit(SiteStore.Events.SiteDeleteError);
    });
  };

  // Store Bindings
  UserStore.on(UserStore.Events.Changed, this._handleUserStoreChanged.bind(this));

  return SiteStore;
}

Store.$inject = [
  '$http',
  '$q',
  '$rootScope',
  '$state',
  '$timeout',
  'APP_DISPATCHER_ID',
  'sbFlux',
  'ResourceHelper',
  'sbAuth',
  'UserStore',
  'AppActions',
  'DashDialogsActions',
  'SiteActions',
  'SiteSelectorActions',
  'IdentityService',
  'StorageUtil'
];

module.exports = Store;



// WEBPACK FOOTER //
// ./src/app/dash/dataStores/SiteStore.js